import React, { useEffect, useState } from "react";
import { Download, Eye, CheckCircle2, XCircle, Unlock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

export default function ImportHistory() {
  const { can } = useAuth();
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [posNo, setPosNo] = useState("");

  const load = () => api.get("/imports").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openDetail = (id) => { api.get(`/imports/${id}`).then((r) => { setDetail(r.data); setPosNo(""); }); };

  const download = async (id, fmt, number) => {
    try {
      const res = await api.get(`/imports/${id}/download`, { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${number}.${fmt === "csv" ? "csv" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal mengunduh"); }
  };

  const markImported = async () => {
    if (!posNo.trim()) return toast.error("Masukkan nomor purchase POS");
    try { await api.post(`/imports/${detail.id}/mark-imported`, { pos_purchase_no: posNo }); toast.success("Ditandai sebagai Imported"); setDetail(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async () => {
    try { await api.post(`/imports/${detail.id}/cancel`); toast.success("Batch dibatalkan"); setDetail(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reopen = async () => {
    try { await api.post(`/imports/${detail.id}/reopen`); toast.success("Batch dibuka kembali (Owner)"); setDetail(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="import-history-page">
      <PageHeader title="Riwayat Import" subtitle="Batch import purchase ke POS" />
      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Batch Import</TableHead><TableHead>Purchase Date</TableHead>
              <TableHead className="text-right">Total Qty</TableHead><TableHead>Status</TableHead>
              <TableHead>No. Purchase POS</TableHead><TableHead>Dibuat</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((b) => (
              <TableRow key={b.id} data-testid={`import-row-${b.id}`}>
                <TableCell className="font-mono text-xs font-semibold text-cocoa">{b.number}</TableCell>
                <TableCell>{b.purchase_date}</TableCell>
                <TableCell className="text-right font-semibold">{b.total_qty}</TableCell>
                <TableCell><StatusBadge status={b.status} /></TableCell>
                <TableCell className="font-mono text-xs">{b.pos_purchase_no || "-"}</TableCell>
                <TableCell className="text-xs text-wmuted">{fmtDate(b.created_at)}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => openDetail(b.id)} data-testid={`view-import-${b.id}`}><Eye className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
            {!list.length && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-10">Belum ada batch import.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>

      {detail && (
        <Dialog open onOpenChange={() => setDetail(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-cocoa flex items-center gap-3">{detail.number} <StatusBadge status={detail.status} /></DialogTitle></DialogHeader>
            <div className="flex gap-2 mb-3">
              <Button size="sm" variant="outline" onClick={() => download(detail.id, "excel", detail.number)} data-testid="download-excel-btn" className="border-taupe text-cocoa"><Download className="w-4 h-4 mr-1.5" /> Excel</Button>
              <Button size="sm" variant="outline" onClick={() => download(detail.id, "csv", detail.number)} data-testid="download-csv-btn" className="border-taupe text-cocoa"><Download className="w-4 h-4 mr-1.5" /> CSV</Button>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent"><TableHead>POS Product ID</TableHead><TableHead>POS Variant ID</TableHead><TableHead>Produk</TableHead><TableHead>W/U</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>QC Batch</TableHead></TableRow></TableHeader>
                <TableBody>
                  {detail.rows.map((r, i) => (
                    <TableRow key={i}><TableCell className="font-mono text-xs">{r.pos_product_id}</TableCell><TableCell className="font-mono text-xs">{r.pos_variant_id}</TableCell><TableCell className="text-sm">{r.product_name}</TableCell><TableCell>{r.color}/{r.size}</TableCell><TableCell className="text-right font-semibold">{r.qty_passed}</TableCell><TableCell className="font-mono text-xs">{r.qc_batch_number}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {detail.status === "Draft" && can("warehouse_admin") && (
              <div className="mt-4 border-t border-taupe/15 pt-4">
                <Label>Nomor Purchase POS (wajib untuk Mark as Imported)</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={posNo} onChange={(e) => setPosNo(e.target.value)} placeholder="PO-POS-xxxx" data-testid="pos-purchase-input" />
                  <Button onClick={markImported} data-testid="mark-imported-btn" className="bg-status-passed hover:bg-green-600 text-white whitespace-nowrap"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark as Imported</Button>
                  <Button variant="outline" onClick={cancel} data-testid="cancel-import-btn" className="border-status-reject text-status-reject"><XCircle className="w-4 h-4 mr-1.5" /> Cancel</Button>
                </div>
              </div>
            )}
            {detail.status === "Imported" && can() && (
              <DialogFooter><Button variant="outline" onClick={reopen} data-testid="reopen-import-btn" className="border-taupe text-cocoa"><Unlock className="w-4 h-4 mr-1.5" /> Buka Kembali (Owner)</Button></DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
